import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { oilApi, oilWsUrl, fmt } from '../components/oil/oilApi';
import { music, sfxOpen, sfxTab } from '../components/oil/oilSounds';
import OilSettingsMenu from '../components/oil/OilSettingsMenu';
import { OIL_LOCALES, OilLocaleProvider, useOilAutoTranslate } from '../i18n/OilLocale';
import { oilArt } from '../i18n/oilArt';
import OilMissions from '../components/oil/OilMissions';
import { Gift, Check } from 'lucide-react';
import OilEmpire from '../components/oil/OilEmpire';
import OilMafia from '../components/oil/OilMafia';
import OilRankings from '../components/oil/OilRankings';
import OilRadio from '../components/oil/OilRadio';
import OilLoading from '../components/oil/OilLoading';
import { preloadCritical, preloadBackground, prioritize } from '../components/oil/oilPreload';
import OilTutorial from '../components/oil/OilTutorial';
import OilResourceCard from '../components/oil/OilResourceCard';
import OilNotice from '../components/oil/OilNotice';

const TABS = [
  { id: 'inicio', label: 'INÍCIO', icon: '/oil/tab_inicio.webp' },
  { id: 'empire', label: 'IMPÉRIO', icon: '/oil/tab_imperio.webp' },
  { id: 'mafia', label: 'MÁFIA', icon: '/oil/tab_mafia.webp' },
  { id: 'ranking', label: 'RANKING', icon: '/oil/tab_ranking.webp' },
];

// trilha sonora de cada aba
const TAB_MUSIC = {
  inicio: '/oil/music_inicio.mp3',
  empire: '/oil/music_imperio.mp3',
  mafia: '/oil/music_mafia.mp3',
  ranking: '/oil/music_ranking.mp3',
};

const countdown = (s) => {
  if (!s || s <= 0) return '00:00:00';
  const d = Math.floor(s / 86400), h = Math.floor((s % 86400) / 3600),
    m = Math.floor((s % 3600) / 60);
  return d > 0 ? `${d}d ${h}h ${m}m` : `${h}h ${m}m ${Math.floor(s % 60)}s`;
};

const fmtC = (n) => {
  n = Number(n || 0);
  if (n >= 1000000) return (n / 1000000).toLocaleString('pt-BR', { maximumFractionDigits: 1 }) + 'M';
  if (n >= 10000) return Math.floor(n / 1000).toLocaleString('pt-BR') + 'K';
  return n.toLocaleString('pt-BR');
};

const CountUp = ({ value }) => {
  const [disp, setDisp] = useState(Number(value || 0));
  const [flash, setFlash] = useState(null);
  const prev = useRef(Number(value || 0));
  useEffect(() => {
    const from = prev.current, to = Number(value || 0);
    prev.current = to;
    if (from === to) return;
    let raf, start = null;
    const tick = (ts) => {
      if (start === null) start = ts;
      const p = Math.min((ts - start) / 700, 1);
      const e = 1 - Math.pow(1 - p, 3);
      setDisp(Math.round(from + (to - from) * e));
      if (p < 1) raf = requestAnimationFrame(tick);
      else { setFlash(to > from ? 'up' : 'down'); setTimeout(() => setFlash(null), 800); }
    };
    const timer = setTimeout(() => { raf = requestAnimationFrame(tick); }, to > from ? 900 : 0);
    return () => { clearTimeout(timer); cancelAnimationFrame(raf); };
  }, [value]);
  return <span className={flash === 'up' ? 'oil-flash' : flash === 'down' ? 'oil-flash-red' : ''}>{fmtC(disp)}</span>;
};

export default function OilTycoonPage() {
  const navigate = useNavigate();
  const [state, setState] = useState(null);
  const [locale, setLocale] = useState(() => localStorage.getItem('oil_locale') || 'pt');
  const gameRootRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [unavailable, setUnavailable] = useState(false);
  const [unavailableMsg, setUnavailableMsg] = useState('Evento indisponível no momento.');
  const [tab, setTab] = useState('inicio');
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [wantedTick, setWantedTick] = useState(0);
  const [rankTick, setRankTick] = useState(0);
  const [showTutorial, setShowTutorial] = useState(() => !localStorage.getItem('oil_tutorial_seen'));
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [missionsOpen, setMissionsOpen] = useState(false);
  const [top1, setTop1] = useState(null);
  const [booted, setBooted] = useState(false);
  const [loadPct, setLoadPct] = useState(0);
  const [resCard, setResCard] = useState(null);
  const visited = useRef({}).current;
  if (tab !== 'inicio') visited[tab] = true;
  const wsRef = useRef(null);

  // música de fundo por aba (volume/mudo no menu da engrenagem)
  useEffect(() => {
    const track = TAB_MUSIC[tab];
    if (!booted) return;
    if (track) { prioritize(track); music.play(track); }
    else music.stop();
    if (tab === 'imperio') {
      prioritize('/oil/video/extract_pumpjack.webm');
      prioritize('/oil/video/bottling_line.webm');
    }
  }, [tab, booted]);
  useEffect(() => () => music.stop(), []);

  // pré-carregamento: imagens + música da abertura antes de liberar a entrada
  useEffect(() => {
    let alive = true;
    const set = (p) => { if (alive) setLoadPct(p); };
    preloadCritical(set).catch(() => set(100));
    // arquivo travado/rede ruim não pode prender o jogador na tela de entrada
    const liberar = setTimeout(() => { if (alive) setLoadPct((p) => (p < 100 ? 100 : p)); }, 20000);
    return () => { alive = false; clearTimeout(liberar); };
  }, []);

  const enterGame = () => {
    try {
      const s = new Audio('/oil/sfx_enter.mp3');
      s.volume = 0.7;
      s.play().catch(() => {});
    } catch { /* sem som */ }
    setBooted(true);
    music.play(TAB_MUSIC.inicio);
    preloadBackground();
  };

  useOilAutoTranslate(gameRootRef, locale);

  const changeLocale = useCallback(async (code) => {
    setLocale(code);
    localStorage.setItem('oil_locale', code);
    try { await oilApi.put('/locale', { locale: code }); } catch { /* segue no aparelho */ }
  }, []);

  const refresh = useCallback(async () => {
    try {
      const r = await oilApi.get('/state');
      setState(r.data);
      if (r.data.game_locale && r.data.game_locale !== localStorage.getItem('oil_locale')) {
        localStorage.setItem('oil_locale', r.data.game_locale);
        setLocale(r.data.game_locale);
      }
      setUnavailable(false);
      if (r.data.season) setSecondsLeft(r.data.season.seconds_left || 0);
    } catch (e) {
      if (e.response?.status === 401) navigate('/conta');
      else if (e.response?.status === 409) {
        setUnavailableMsg(e.response?.data?.detail || 'Evento indisponível no momento.');
        setUnavailable(true);
      }
    }
    setLoading(false);
  }, [navigate]);

  useEffect(() => {
    if (!localStorage.getItem('customer_token')) { navigate('/conta'); return; }
    refresh();
  }, [refresh, navigate]);

  useEffect(() => {
    const t = setInterval(() => setSecondsLeft((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    oilApi.get('/rankings').then((r) => {
      setTop1(r.data.magnates?.[0] || null);
      const meRank = r.data.magnates_me?.rank ?? null;
      setState((s) => (s && s.rank !== meRank ? { ...s, rank: meRank } : s));
    }).catch(() => {});
  }, [rankTick]);

  useEffect(() => {
    let alive = true, fails = 0, pingTimer = null;
    const connect = () => {
      if (!alive) return;
      try {
        const ws = new WebSocket(oilWsUrl());
        wsRef.current = ws;
        ws.onopen = () => {
          fails = 0;
          clearInterval(pingTimer);
          pingTimer = setInterval(() => { try { ws.send('ping'); } catch {} }, 25000);
        };
        ws.onmessage = (ev) => {
          try {
            const msg = JSON.parse(ev.data);
            if (msg.type === 'oil.wanted.updated' || msg.type === 'oil.attack.completed') setWantedTick((t) => t + 1);
            if (msg.type === 'oil.rank.updated' || msg.type === 'oil.extraction.completed'
                || msg.type === 'oil.attack.completed') setRankTick((t) => t + 1);
            if (msg.type === 'oil.jackpot.updated') {
              setRankTick((t) => t + 1);
              const jp = msg.data?.jackpot;
              if (jp) setState((s) => (s?.season ? { ...s, season: { ...s.season, jackpot: jp } } : s));
            }
          } catch {}
        };
        ws.onerror = () => {};
        ws.onclose = () => {
          clearInterval(pingTimer);
          if (!alive) return;
          fails += 1;
          setTimeout(connect, Math.min(3000 * fails, 30000));
        };
      } catch { if (alive) setTimeout(connect, 5000); }
    };
    connect();
    return () => {
      alive = false;
      clearInterval(pingTimer);
      const ws = wsRef.current;
      if (ws) {
        if (ws.readyState === WebSocket.OPEN) ws.close();
        else ws.onopen = () => ws.close();
      }
    };
  }, []);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#0a0d12]">
        {booted
          ? <Loader2 className="h-8 w-8 animate-spin text-amber-500" />
          : <OilLoading progress={loadPct} onEnter={enterGame} />}
      </div>
    );
  }

  if (unavailable) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-[#0a0d12] px-6 text-center"
        data-testid="oil-unavailable">
        <span className="text-6xl">🚧</span>
        <h1 className="mt-4 text-2xl font-black tracking-wider text-amber-400">OIL TYCOON</h1>
        <p className="mt-2 text-sm text-zinc-400" data-testid="oil-unavailable-msg">
          {unavailableMsg}<br />Volte mais tarde — a gente avisa quando o petróleo voltar a jorrar. ⛽
        </p>
        <button onClick={() => navigate('/conta')} data-testid="oil-unavailable-back"
          className="mt-6 rounded-full border border-amber-600 px-6 py-2 text-xs font-black text-amber-400">
          ← Voltar à minha conta
        </button>
      </div>
    );
  }

  if (!state?.season) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-[#0a0d12] px-6 text-center" data-testid="oil-no-season">
        <span className="text-6xl">🚫</span>
        <h1 className="mt-4 text-2xl font-black text-amber-400 tracking-wider">OIL TYCOON</h1>
        <p className="mt-3 inline-block rounded-full border border-rose-500/70 bg-rose-950/60 px-4 py-1 text-xs font-black uppercase tracking-[0.2em] text-rose-300" data-testid="oil-inactive-badge">
          Evento inativo
        </p>
        <p className="mt-3 text-sm text-zinc-400" data-testid="oil-inactive-msg">
          O OIL TYCOON não está disponível no momento.<br />Fique de olho — a gente avisa quando o petróleo voltar a jorrar! ⛽
        </p>
        <button onClick={() => navigate('/conta')} data-testid="oil-inactive-back" className="mt-6 rounded-full border border-amber-600 px-6 py-2 text-xs font-black text-amber-400">
          ← Voltar à minha conta
        </button>
      </div>
    );
  }

  const { player, season } = state;
  const canChangeLocale = !!state.is_test_account;
  const missionsAlert = state.missions?.claimable || 0;
  const missionsDone = !!state.missions?.all_done;

  return (
    <OilLocaleProvider locale={locale} setLocale={changeLocale} canChange={canChangeLocale}>
    <div ref={gameRootRef} className="min-h-screen bg-[#0a0d12] text-white" data-testid="oil-tycoon-page">
      {/* desktop: o jogo é mobile vertical — fora da moldura fica arte desfocada */}
      <div className="pointer-events-none fixed inset-0 z-0 hidden md:block" aria-hidden="true"
        data-testid="oil-desktop-backdrop">
        <img src="/oil/hero.webp" alt="" className="h-full w-full scale-110 object-cover opacity-20 blur-2xl" />
        <div className="absolute inset-0 bg-[#05070b]/85" />
      </div>
      <div className="pointer-events-none fixed inset-y-0 left-1/2 z-[45] hidden w-full max-w-md -translate-x-1/2 border-x border-amber-900/40 shadow-[0_0_80px_rgba(0,0,0,0.95)] md:block"
        aria-hidden="true" data-testid="oil-phone-frame" />
      {!booted && <OilLoading progress={loadPct} onEnter={enterGame} />}
      {booted && showTutorial && <OilTutorial onClose={() => setShowTutorial(false)} />}
      {missionsOpen && <OilMissions onClose={() => setMissionsOpen(false)} refresh={refresh} />}
      <OilNotice />
      {resCard && (
        <OilResourceCard res={resCard} onClose={() => setResCard(null)}
          value={{ black: player.black_coins, drops: player.drops, fox: player.fox_dollars, respect: player.respect }[resCard]} />
      )}
      {/* fundo fullscreen da aba INÍCIO */}
      {tab === 'inicio' && (
        <div className="fixed inset-y-0 left-1/2 z-0 w-full max-w-md -translate-x-1/2" data-testid="oil-inicio-bg">
          <img src={oilArt('hero', locale)} alt="OIL TYCOON"
            className="h-full w-full object-cover object-top" data-testid="oil-opening-image" />
          <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-black/85 to-transparent" />
          {/* CRONÔMETRO destacado — abaixo de A BOLSA DO PETRÓLEO (encerrada: aviso no lugar do tempo) */}
          <div className="absolute inset-x-0 top-[51.5%] flex justify-center">
            {season.ended ? (
              <div className="rounded-xl border border-rose-500/80 bg-black px-3 py-1 text-center shadow-[0_0_10px_rgba(244,63,94,0.4)]" data-testid="oil-ended-badge">
                <p className="text-[7px] font-black uppercase tracking-[0.25em] text-rose-400">Season encerrada</p>
                <p className="text-xs font-black tracking-wider text-rose-200 drop-shadow">🏁 Confira o resultado final no Ranking</p>
              </div>
            ) : (
              <div className="rounded-xl border border-amber-500/80 bg-black px-3 py-1 text-center shadow-[0_0_10px_rgba(255,180,0,0.35)]" data-testid="oil-countdown">
                <p className="text-[7px] font-black uppercase tracking-[0.25em] text-amber-500">A Season termina em</p>
                <p className="text-xs font-black tracking-wider text-amber-300 drop-shadow">⏳ {countdown(secondsLeft)}</p>
              </div>
            )}
          </div>
          {/* Moldura TOP 1 MAGNATA */}
          {top1 && (
            <div className="absolute right-2 top-[59%] w-[118px] -translate-y-1/2" data-testid="oil-top1-widget">
              <div className="relative rounded-full" style={{ aspectRatio: '1/1', animation: 'top1-glow 2.2s ease-in-out infinite' }}>
                <div className="absolute overflow-hidden rounded-full" style={{ left: '26.8%', top: '21.8%', width: '46.7%', aspectRatio: '1/1' }}>
                  {top1.avatar
                    ? <img src={top1.avatar} alt={top1.nickname} className="h-full w-full object-cover" />
                    : <div className="flex h-full w-full items-center justify-center bg-zinc-900 text-2xl">🦊</div>}
                </div>
                <img src={oilArt('top1_frame', locale)} alt="TOP 1" className="absolute inset-0 h-full w-full drop-shadow-2xl" />
              </div>
              <p className="mx-auto -mt-1 w-fit max-w-full truncate rounded-full border border-amber-600/70 bg-black/80 px-2.5 py-0.5 text-center text-[9px] font-black text-amber-300 backdrop-blur"
                data-testid="oil-top1-nickname">
                {top1.nickname}
              </p>
              <style>{`@keyframes top1-glow {
                0%, 100% { filter: drop-shadow(0 0 6px rgba(255,196,0,0.55)) drop-shadow(0 0 14px rgba(255,140,0,0.35)); }
                50% { filter: drop-shadow(0 0 14px rgba(255,220,80,0.95)) drop-shadow(0 0 30px rgba(255,160,0,0.6)); }
              }`}</style>
            </div>
          )}
        </div>
      )}
      {/* fundos fullscreen (sempre montados: alternar aba não recarrega arte) */}
      {[
        { id: 'empire', src: '/oil/bg_imperio.webp', testid: 'oil-empire-bg', veil: 'from-black/50 via-black/30' },
        { id: 'mafia', src: '/oil/bg_mafia.webp', testid: 'oil-mafia-bg', veil: 'from-black/45 via-black/30' },
        { id: 'ranking', src: '/oil/bg_ranking.webp', testid: 'oil-ranking-bg', veil: 'from-black/50 via-black/35' },
      ].map((b) => (
        <div key={b.id} data-testid={b.testid}
          className={`pointer-events-none fixed inset-y-0 left-1/2 z-0 w-full max-w-md -translate-x-1/2 transition-opacity duration-200 ${tab === b.id ? 'opacity-100' : 'opacity-0'}`}>
          <img src={b.src} alt="" className="h-full w-full object-cover object-top" />
          <div className={`absolute inset-0 bg-gradient-to-b ${b.veil} to-[#0a0d12]/90`} />
        </div>
      ))}
      {/* ===== TOPO: HUD BAR (abas internas) ou engrenagem solta (Início) ===== */}
      <div className="pwa-safe-top sticky top-0 z-40 w-full" data-testid="oil-hud-sticky">
        {state?.is_test_account && (
          <div className="mx-auto w-full max-w-md px-2 pb-1" data-testid="oil-test-account-badge">
            <p className="rounded-full border border-violet-400/70 bg-violet-950/80 py-0.5 text-center text-[9px] font-black tracking-[0.18em] text-violet-200 backdrop-blur">
              CONTA DE TESTE · FORA DE RANKING E PRÊMIOS
            </p>
          </div>
        )}
        {season.ended && tab !== 'inicio' && (
          <div className="mx-auto w-full max-w-md px-2 pb-1" data-testid="oil-ended-banner">
            <p className="rounded-full border border-rose-400/70 bg-rose-950/85 py-0.5 text-center text-[9px] font-black tracking-[0.18em] text-rose-200 backdrop-blur">
              SEASON ENCERRADA · SÓ CONSULTA — RESULTADO FINAL NO RANKING
            </p>
          </div>
        )}
        <div className="relative mx-auto w-full max-w-md select-none">
        {tab === 'inicio' ? (
          <div className="relative w-full" style={{ aspectRatio: '1973/325' }}>
            <button onClick={() => { sfxOpen(); setMissionsOpen(true); }} data-testid="oil-missions-btn"
              className="absolute flex items-center gap-1.5 rounded-full border border-amber-500/80 bg-black/75 px-2.5 py-1.5 shadow-lg backdrop-blur transition-transform active:scale-95"
              style={{ left: '3%', top: '16%' }}>
              <Gift className="h-3.5 w-3.5 text-amber-400" />
              <span className="text-[10px] font-black tracking-widest text-amber-200">MISSÕES</span>
              {missionsAlert > 0 ? (
                <span data-testid="oil-missions-badge"
                  className="flex h-4 min-w-[16px] animate-pulse items-center justify-center rounded-full bg-red-600 px-1 text-[9px] font-black text-white">
                  {missionsAlert}
                </span>
              ) : missionsDone && (
                <span data-testid="oil-missions-badge-done"
                  className="flex h-4 w-4 items-center justify-center rounded-full bg-emerald-500 text-white shadow-[0_0_10px_rgba(16,185,129,0.7)]">
                  <Check className="h-3 w-3" strokeWidth={4} />
                </span>
              )}
            </button>
            <button onClick={() => setSettingsOpen((o) => !o)} data-testid="oil-settings-btn" aria-label="Configurações"
              className="absolute flex items-center justify-center transition-transform active:scale-95" style={{ left: '87.5%', top: '20%', width: '10%', height: '46%' }}>
              <img src="/oil/gear.webp" alt="Configurações" className="h-full w-auto drop-shadow-xl" />
            </button>
          </div>
        ) : (
        <div className="relative w-full" style={{ aspectRatio: '1973/325' }} data-testid="oil-hud-bar">
          <img src="/oil/hud_bar.webp" alt="" className="absolute inset-0 h-full w-full drop-shadow-xl" />
          {/* avatar no círculo (mapeado por pixel) */}
          <div className="absolute overflow-hidden rounded-full" style={{ left: '1.65%', top: '62%', width: '4.45%', aspectRatio: '1/1' }}>
            {player.avatar
              ? <img src={player.avatar} alt="" className="h-full w-full object-cover" />
              : <div className="flex h-full w-full items-center justify-center bg-zinc-900 text-[9px]">🦊</div>}
          </div>
          {/* nickname na pill */}
          <div className="absolute flex items-center justify-center" style={{ left: '6.5%', top: '67%', width: '10.8%', height: '13%' }}>
            <p className="w-full truncate text-center font-black text-amber-100" data-testid="oil-player-name"
              style={{ fontSize: 'clamp(4px, 1.55vw, 8px)', lineHeight: 1 }}>{player.nickname}</p>
          </div>
          {/* 4 slots (mapeados por pixel) */}
          {[
            { key: 'black', left: '20.5%', width: '14.1%', val: player.black_coins, cls: 'text-amber-300' },
            { key: 'drops', left: '36.8%', width: '14.4%', val: player.drops, cls: 'text-zinc-100' },
            { key: 'fox', left: '53.2%', width: '14.3%', val: player.fox_dollars, cls: 'text-emerald-300' },
            { key: 'respect', left: '69.7%', width: '14.3%', val: player.respect, cls: 'text-rose-300' },
          ].map((s) => (
            <button key={s.key} onClick={() => { sfxOpen(); setResCard(s.key); }} data-testid={`oil-hud-slot-${s.key}`}
              className="absolute flex items-center justify-center gap-[0.3em] transition-transform active:scale-90" style={{ left: `calc(${s.left} + 1.2%)`, top: '34%', width: `calc(${s.width} - 2.4%)`, height: '27%', fontSize: 'clamp(6px, 2.05vw, 10px)' }}>
              <img src={`/oil/ic_${s.key}.webp`} alt="" className="h-[1.3em] w-auto shrink-0" />
              <p className={`truncate font-black ${s.cls}`} data-testid={`oil-hud-${s.key}`} style={{ lineHeight: 1 }}>
                <CountUp value={s.val} />
              </p>
            </button>
          ))}
          {/* engrenagem → configurações */}
          <button onClick={() => setSettingsOpen((o) => !o)} data-testid="oil-settings-btn" aria-label="Configurações"
            className="absolute flex items-center justify-center transition-transform active:scale-95" style={{ left: '87.5%', top: '20%', width: '10%', height: '46%' }}>
            <img src="/oil/gear.webp" alt="Configurações" className="h-full w-auto drop-shadow-lg" />
          </button>
        </div>
        )}
          {tab !== 'inicio' && <OilRadio tick={rankTick} />}
          {settingsOpen && (
            <div className="absolute right-1 top-full z-50 mt-1 rounded-xl border border-amber-800/70 bg-[#12100a] p-3 shadow-2xl" data-testid="oil-settings-menu">
              <OilSettingsMenu
                onExit={() => navigate('/conta')}
                locale={locale}
                localeOptions={canChangeLocale ? OIL_LOCALES : []}
                onLocale={changeLocale}
                footer={(
                  <p className="text-center text-[9px] text-zinc-500">
                    {state.rank ? `Você é #${state.rank} entre os Magnatas` : 'Sem posição no ranking ainda'}
                    {player.shields > 0 && ` · 🛡️×${player.shields}`}
                  </p>
                )}
              />
            </div>
          )}
        </div>
      </div>

      <div className="relative z-10 mx-auto max-w-md px-3 pb-24 pt-1.5">
        {/* ← conta agora fica no menu da engrenagem */}
        {(season.active_global_events || []).length > 0 && (
          <div className="mt-1.5 rounded-lg border border-amber-700 bg-amber-950/60 px-2 py-1 text-center backdrop-blur" data-testid="oil-global-event-banner">
            <p className="text-[10px] font-black text-amber-300 animate-pulse">
              ⚡ EVENTO GLOBAL ATIVO: {season.active_global_events.map((e) => `${e.id} +${e.bonus_pct}%`).join(' · ')}
            </p>
          </div>
        )}

        {/* ===== Conteúdo — fica montado depois da 1ª visita: trocar aba não remonta ===== */}
        <div className="mt-4">
          {visited.empire && (
            <div style={{ display: tab === 'empire' ? 'block' : 'none' }}>
              <OilEmpire state={state} refresh={refresh} />
            </div>
          )}
          {visited.mafia && (
            <div style={{ display: tab === 'mafia' ? 'block' : 'none' }}>
              <OilMafia state={state} refresh={refresh} wantedTick={wantedTick} />
            </div>
          )}
          {visited.ranking && (
            <div style={{ display: tab === 'ranking' ? 'block' : 'none' }}>
              <OilRankings state={state} rankTick={rankTick} active={tab === 'ranking'} />
            </div>
          )}
        </div>
      </div>

      {/* ===== Bottom nav ===== */}
      <nav className="pwa-safe-bottom fixed bottom-0 left-1/2 z-50 w-full max-w-md -translate-x-1/2 border-t border-zinc-800 bg-[#0c0f14]/95 backdrop-blur">
        <div className="mx-auto flex max-w-md">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => { if (t.id !== tab) sfxTab(); setTab(t.id); }} data-testid={`oil-tab-${t.id}`}
              className={`flex flex-1 flex-col items-center gap-0.5 py-2.5 transition-colors ${tab === t.id ? 'text-amber-400' : 'text-zinc-600'}`}>
              <span className="flex h-8 w-8 items-center justify-center">
                <img src={t.icon} alt={t.label}
                  className={`max-h-8 max-w-8 object-contain transition-all ${tab === t.id ? '' : 'opacity-50 grayscale-[0.4] brightness-75'}`} />
              </span>
              <span className="text-[9px] font-black tracking-wider">{t.label}</span>
              {tab === t.id && <span className="h-0.5 w-8 rounded-full bg-amber-500" />}
            </button>
          ))}
        </div>
      </nav>
    </div>
    </OilLocaleProvider>
  );
}
